import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Check, Sparkles, Zap, 
  Mail, Phone, User, Building, Download, Calculator 
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import SEO from '../components/SEO';
import emailjs from '@emailjs/browser';

function PricingCalculator() {
  const { language } = useLanguage();
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    projectType: '',
    features: [],
    timeline: '',
    budget: '',
    design: '',
    maintenance: '',
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  const totalSteps = 6;

  // Pricing logic
  const calculatePrice = () => {
    let basePrice = 0;
    let minPrice = 0;
    let maxPrice = 0;

    // Base price by project type
    const projectPrices = {
      website: { base: 800, min: 500, max: 1500 },
      ecommerce: { base: 2000, min: 1500, max: 4000 },
      webapp: { base: 3500, min: 2500, max: 6000 },
      mobileapp: { base: 4000, min: 3000, max: 7000 },
      branding: { base: 600, min: 400, max: 1200 },
      marketing: { base: 500, min: 300, max: 1000 }
    };

    if (answers.projectType && projectPrices[answers.projectType]) {
      basePrice = projectPrices[answers.projectType].base;
      minPrice = projectPrices[answers.projectType].min;
      maxPrice = projectPrices[answers.projectType].max;
    }

    // Add features cost
    const featurePrices = {
      blog: 200,
      gallery: 150,
      booking: 400,
      payment: 500,
      crm: 600,
      analytics: 200,
      multilang: 300,
      seo: 250,
      social: 100,
      chat: 150
    };

    answers.features.forEach(feature => {
      if (featurePrices[feature]) {
        basePrice += featurePrices[feature];
        minPrice += featurePrices[feature] * 0.7;
        maxPrice += featurePrices[feature] * 1.3;
      }
    });

    // Timeline multiplier
    const timelineMultipliers = {
      urgent: 1.3,
      normal: 1,
      flexible: 0.9
    };

    if (answers.timeline && timelineMultipliers[answers.timeline]) {
      const mult = timelineMultipliers[answers.timeline];
      basePrice *= mult;
      minPrice *= mult;
      maxPrice *= mult;
    }

    // Design complexity
    const designPrices = {
      basic: 0,
      standard: 300,
      premium: 700,
      custom: 1200
    };

    if (answers.design && designPrices[answers.design]) {
      const price = designPrices[answers.design];
      basePrice += price;
      minPrice += price * 0.8;
      maxPrice += price * 1.2;
    }

    // Maintenance
    const maintenancePrices = {
      none: 0,
      basic: 100,
      standard: 200,
      premium: 400
    };

    let monthlyMaintenance = 0;
    if (answers.maintenance && maintenancePrices[answers.maintenance]) {
      monthlyMaintenance = maintenancePrices[answers.maintenance];
    }

    return {
      estimated: Math.round(basePrice),
      min: Math.round(minPrice),
      max: Math.round(maxPrice),
      monthly: monthlyMaintenance
    };
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const pricing = calculatePrice();
    
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_PRICING_TEMPLATE_ID,
        {
          from_name: answers.name,
          from_email: answers.email,
          from_phone: answers.phone,
          from_company: answers.company,
          project_type: answers.projectType,
          features: answers.features.join(', '),
          timeline: answers.timeline,
          budget: answers.budget,
          design: answers.design,
          maintenance: answers.maintenance,
          estimated_price: `€${pricing.estimated}`,
          price_range: `€${pricing.min} - €${pricing.max}`,
          monthly_maintenance: pricing.monthly > 0 ? `€${pricing.monthly}/month` : 'None',
          to_name: 'Contenta Marketing Team'
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      showToast(
        language === 'sq' 
          ? 'Oferta u dërgua me sukses! Do të kontaktohemi së shpejti.' 
          : 'Quote sent successfully! We\'ll contact you soon.',
        'success'
      );
    } catch (error) {
      console.error('Error:', error);
      showToast(
        language === 'sq' 
          ? 'Gabim në dërgim. Ju lutem provoni përsëri.' 
          : 'Error sending. Please try again.',
        'error'
      );
    }
  };

  const content = {
    sq: {
      title: 'Llogaritësi i Çmimit',
      subtitle: 'Zbulo sa kushton projekti yt në vetëm 2 minuta',
      steps: {
        1: {
          title: 'Lloji i Projektit',
          subtitle: 'Zgjidhni llojin e projektit që dëshironi',
          options: [
            { id: 'website', label: 'Website / Landing Page', desc: 'Faqe statike ose dinamike' },
            { id: 'ecommerce', label: 'E-Commerce Platform', desc: 'Dyqan online me pagesa' },
            { id: 'webapp', label: 'Web Application', desc: 'Platform kompleks, dashboard' },
            { id: 'mobileapp', label: 'Mobile App', desc: 'iOS dhe Android aplikacion' },
            { id: 'branding', label: 'Brand Identity', desc: 'Logo, brand guidelines' },
            { id: 'marketing', label: 'Digital Marketing', desc: 'SEO, social media, ads' }
          ]
        },
        2: {
          title: 'Features & Funksione',
          subtitle: 'Zgjidhni features që dëshironi (mund të zgjidhni shumë)',
          options: [
            { id: 'blog', label: 'Blog / News Section', icon: '📝' },
            { id: 'gallery', label: 'Photo/Video Gallery', icon: '🖼️' },
            { id: 'booking', label: 'Booking System', icon: '📅' },
            { id: 'payment', label: 'Payment Integration', icon: '💳' },
            { id: 'crm', label: 'CRM Integration', icon: '📊' },
            { id: 'analytics', label: 'Analytics Dashboard', icon: '📈' },
            { id: 'multilang', label: 'Multi-Language', icon: '🌍' },
            { id: 'seo', label: 'SEO Optimization', icon: '🔍' },
            { id: 'social', label: 'Social Media Integration', icon: '📱' },
            { id: 'chat', label: 'Live Chat', icon: '💬' }
          ]
        },
        3: {
          title: 'Timeline',
          subtitle: 'Kur dëshironi të jetë gati projekti?',
          options: [
            { id: 'urgent', label: 'Sa më shpejt (1-2 javë)', desc: '+30% çmim për prioritet' },
            { id: 'normal', label: 'Normal (3-6 javë)', desc: 'Timeline standard' },
            { id: 'flexible', label: 'Fleksibël (6+ javë)', desc: '-10% discount' }
          ]
        },
        4: {
          title: 'Kompleksiteti i Design-it',
          subtitle: 'Sa kompleks dëshironi design-in?',
          options: [
            { id: 'basic', label: 'Basic', desc: 'Template-based, minimal customization' },
            { id: 'standard', label: 'Standard', desc: 'Custom design, brand colors' },
            { id: 'premium', label: 'Premium', desc: 'Unique design, animations' },
            { id: 'custom', label: 'Fully Custom', desc: 'Completely unique, advanced animations' }
          ]
        },
        5: {
          title: 'Maintenance & Support',
          subtitle: 'Dëshironi maintenance të vazhdueshëm?',
          options: [
            { id: 'none', label: 'Asnjë', desc: 'Vetëm zhvillimi, pa support' },
            { id: 'basic', label: 'Basic', desc: '€100/muaj - Updates, backups' },
            { id: 'standard', label: 'Standard', desc: '€200/muaj - Updates, security, content changes' },
            { id: 'premium', label: 'Premium', desc: '€400/muaj - Full support, analytics, optimization' }
          ]
        }
      },
      contactForm: {
        title: 'Informacionet Tuaja',
        subtitle: 'Plotësoni për të marrë ofertën e detajuar',
        name: 'Emri Juaj',
        email: 'Email',
        phone: 'Telefon',
        company: 'Kompania (opsionale)',
        submit: 'Merr Ofertën',
        submitting: 'Duke dërguar...'
      },
      buttons: {
        next: 'Vazhdo',
        back: 'Prapa',
        calculate: 'Llogarit Çmimin'
      }
    },
    en: {
      title: 'Pricing Calculator',
      subtitle: 'Discover your project cost in just 2 minutes',
      steps: {
        1: {
          title: 'Project Type',
          subtitle: 'Choose the type of project you need',
          options: [
            { id: 'website', label: 'Website / Landing Page', desc: 'Static or dynamic pages' },
            { id: 'ecommerce', label: 'E-Commerce Platform', desc: 'Online store with payments' },
            { id: 'webapp', label: 'Web Application', desc: 'Complex platform, dashboard' },
            { id: 'mobileapp', label: 'Mobile App', desc: 'iOS and Android application' },
            { id: 'branding', label: 'Brand Identity', desc: 'Logo, brand guidelines' },
            { id: 'marketing', label: 'Digital Marketing', desc: 'SEO, social media, ads' }
          ]
        },
        2: {
          title: 'Features & Functions',
          subtitle: 'Select desired features (can choose multiple)',
          options: [
            { id: 'blog', label: 'Blog / News Section', icon: '📝' },
            { id: 'gallery', label: 'Photo/Video Gallery', icon: '🖼️' },
            { id: 'booking', label: 'Booking System', icon: '📅' },
            { id: 'payment', label: 'Payment Integration', icon: '💳' },
            { id: 'crm', label: 'CRM Integration', icon: '📊' },
            { id: 'analytics', label: 'Analytics Dashboard', icon: '📈' },
            { id: 'multilang', label: 'Multi-Language', icon: '🌍' },
            { id: 'seo', label: 'SEO Optimization', icon: '🔍' },
            { id: 'social', label: 'Social Media Integration', icon: '📱' },
            { id: 'chat', label: 'Live Chat', icon: '💬' }
          ]
        },
        3: {
          title: 'Timeline',
          subtitle: 'When do you need the project ready?',
          options: [
            { id: 'urgent', label: 'ASAP (1-2 weeks)', desc: '+30% price for priority' },
            { id: 'normal', label: 'Normal (3-6 weeks)', desc: 'Standard timeline' },
            { id: 'flexible', label: 'Flexible (6+ weeks)', desc: '-10% discount' }
          ]
        },
        4: {
          title: 'Design Complexity',
          subtitle: 'How complex should the design be?',
          options: [
            { id: 'basic', label: 'Basic', desc: 'Template-based, minimal customization' },
            { id: 'standard', label: 'Standard', desc: 'Custom design, brand colors' },
            { id: 'premium', label: 'Premium', desc: 'Unique design, animations' },
            { id: 'custom', label: 'Fully Custom', desc: 'Completely unique, advanced animations' }
          ]
        },
        5: {
          title: 'Maintenance & Support',
          subtitle: 'Do you want ongoing maintenance?',
          options: [
            { id: 'none', label: 'None', desc: 'Development only, no support' },
            { id: 'basic', label: 'Basic', desc: '€100/month - Updates, backups' },
            { id: 'standard', label: 'Standard', desc: '€200/month - Updates, security, content changes' },
            { id: 'premium', label: 'Premium', desc: '€400/month - Full support, analytics, optimization' }
          ]
        }
      },
      contactForm: {
        title: 'Your Information',
        subtitle: 'Fill in to receive detailed quote',
        name: 'Your Name',
        email: 'Email',
        phone: 'Phone',
        company: 'Company (optional)',
        submit: 'Get Quote',
        submitting: 'Sending...'
      },
      buttons: {
        next: 'Next',
        back: 'Back',
        calculate: 'Calculate Price'
      }
    }
  };

  const t = content[language];
  const pricing = calculatePrice();

  return (
    <>
      <SEO 
        title={t.title}
        description={t.subtitle}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 sm:py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <Calculator className="w-4 h-4" />
              {t.title}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t.subtitle}
            </h1>

            {/* Progress Bar */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {[1, 2, 3, 4, 5, 6].map((s) => (
                <div
                  key={s}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    s === step 
                      ? 'w-12 bg-gradient-to-r from-yellow-400 to-yellow-600' 
                      : s < step 
                        ? 'w-8 bg-yellow-400' 
                        : 'w-8 bg-gray-300 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {language === 'sq' ? 'Hapi' : 'Step'} {step} {language === 'sq' ? 'nga' : 'of'} {totalSteps}
            </p>
          </motion.div>

          {/* Steps */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-10"
            >
              {/* Step 1-5: Questions */}
              {step <= 5 && (
                <>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {t.steps[step].title}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-8">
                    {t.steps[step].subtitle}
                  </p>

                  {/* Step 1: Project Type */}
                  {step === 1 && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {t.steps[1].options.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setAnswers({ ...answers, projectType: option.id })}
                          className={`p-6 rounded-xl border-2 text-left transition-all duration-300 ${
                            answers.projectType === option.id
                              ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 shadow-lg'
                              : 'border-gray-200 dark:border-gray-700 hover:border-yellow-300 dark:hover:border-yellow-700'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-bold text-gray-900 dark:text-white">{option.label}</h3>
                            {answers.projectType === option.id && (
                              <Check className="w-6 h-6 text-yellow-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{option.desc}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Step 2: Features */}
                  {step === 2 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {t.steps[2].options.map((option) => {
                        const isSelected = answers.features.includes(option.id);
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              if (isSelected) {
                                setAnswers({
                                  ...answers,
                                  features: answers.features.filter(f => f !== option.id)
                                });
                              } else {
                                setAnswers({
                                  ...answers,
                                  features: [...answers.features, option.id]
                                });
                              }
                            }}
                            className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                              isSelected
                                ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:border-yellow-300 dark:hover:border-yellow-700'
                            }`}
                          >
                            <div className="text-3xl mb-2">{option.icon}</div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                              {option.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Step 3, 4, 5: Single choice */}
                  {(step === 3 || step === 4 || step === 5) && (
                    <div className="space-y-3">
                      {t.steps[step].options.map((option) => {
                        const fieldName = step === 3 ? 'timeline' : step === 4 ? 'design' : 'maintenance';
                        const isSelected = answers[fieldName] === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => setAnswers({ ...answers, [fieldName]: option.id })}
                            className={`w-full p-5 rounded-xl border-2 text-left transition-all duration-300 ${
                              isSelected
                                ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 shadow-lg'
                                : 'border-gray-200 dark:border-gray-700 hover:border-yellow-300 dark:hover:border-yellow-700'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{option.label}</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{option.desc}</p>
                              </div>
                              {isSelected && (
                                <Check className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Step 6: Contact Form & Results */}
              {step === 6 && (
                <>
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Pricing Result */}
                    <div className="lg:order-2">
                      <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-white sticky top-4">
                        <div className="flex items-center gap-2 mb-4">
                          <Sparkles className="w-6 h-6" />
                          <h3 className="text-xl font-bold">
                            {language === 'sq' ? 'Çmimi i Vlerësuar' : 'Estimated Price'}
                          </h3>
                        </div>

                        <div className="bg-white/20 backdrop-blur rounded-lg p-6 mb-4">
                          <div className="text-center">
                            <div className="text-sm opacity-90 mb-2">
                              {language === 'sq' ? 'Nga' : 'From'}
                            </div>
                            <div className="text-5xl font-bold mb-2">€{pricing.estimated}</div>
                            <div className="text-sm opacity-90">
                              {language === 'sq' ? 'Range' : 'Range'}: €{pricing.min} - €{pricing.max}
                            </div>
                          </div>
                        </div>

                        {pricing.monthly > 0 && (
                          <div className="bg-white/20 backdrop-blur rounded-lg p-4 mb-4">
                            <div className="text-sm opacity-90 mb-1">
                              {language === 'sq' ? 'Maintenance Mujor' : 'Monthly Maintenance'}
                            </div>
                            <div className="text-2xl font-bold">€{pricing.monthly}/mo</div>
                          </div>
                        )}

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4" />
                            <span>{language === 'sq' ? 'Garanci 100% Kënaqësie' : '100% Satisfaction Guarantee'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4" />
                            <span>{language === 'sq' ? 'Support 24/7' : '24/7 Support'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4" />
                            <span>{language === 'sq' ? 'Revizione Unlimited' : 'Unlimited Revisions'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:order-1">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {t.contactForm.title}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        {t.contactForm.subtitle}
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            {t.contactForm.name} *
                          </label>
                          <input
                            type="text"
                            required
                            value={answers.name}
                            onChange={(e) => setAnswers({ ...answers, name: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            {t.contactForm.email} *
                          </label>
                          <input
                            type="email"
                            required
                            value={answers.email}
                            onChange={(e) => setAnswers({ ...answers, email: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            {t.contactForm.phone} *
                          </label>
                          <input
                            type="tel"
                            required
                            value={answers.phone}
                            onChange={(e) => setAnswers({ ...answers, phone: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            {t.contactForm.company}
                          </label>
                          <input
                            type="text"
                            value={answers.company}
                            onChange={(e) => setAnswers({ ...answers, company: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          />
                        </div>

                        <button
                          type="submit"
                          className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                          <Mail className="w-5 h-5" />
                          {t.contactForm.submit}
                        </button>
                      </form>
                    </div>
                  </div>
                </>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                {step > 1 && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-semibold transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    {t.buttons.back}
                  </button>
                )}

                {step < 6 && (
                  <button
                    onClick={handleNext}
                    disabled={
                      (step === 1 && !answers.projectType) ||
                      (step === 2 && answers.features.length === 0) ||
                      (step === 3 && !answers.timeline) ||
                      (step === 4 && !answers.design) ||
                      (step === 5 && !answers.maintenance)
                    }
                    className="ml-auto flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {step === 5 ? t.buttons.calculate : t.buttons.next}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

export default PricingCalculator;